/**
 * Componente: Modal de Selección de Ciudad o Municipio — CiudadModalSelector
 * Fiel al diseño de /root/Avifacil_Planeacion_proyecto/desing frontend/design.moal-ciudad.html
 * - Centrado en pantalla con fondo blur neutro esmerilado (no morado).
 * - Alimentado por `country-state-city` a través del servicio geo.
 * - Búsqueda en tiempo real, scrollbar lila/violeta y checkmark de selección.
 */

'use client';

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './CiudadModalSelector.module.css';

export interface CiudadItem {
  value: string;
  label: string;
}

export interface CiudadModalSelectorProps {
  isOpen: boolean;
  selectedCityName?: string;
  countryName?: string;
  stateName?: string;
  cities: CiudadItem[];
  isLoading?: boolean;
  onSelect: (cityName: string) => void;
  onClose: () => void;
  onOpenStateModal?: () => void;
}

export function CiudadModalSelector({
  isOpen,
  selectedCityName,
  countryName,
  stateName,
  cities,
  isLoading = false,
  onSelect,
  onClose,
  onOpenStateModal,
}: CiudadModalSelectorProps) {
  const [search, setSearch] = useState('');
  const [mounted, setMounted] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Filtrado reactivo en tiempo real
  const filteredCities = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return cities;

    return cities.filter((c) => {
      const name = (c.label || c.value || '').toLowerCase();
      return name.includes(query);
    });
  }, [cities, search]);

  // Manejo de foco automático al abrir
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Cerrar con tecla Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !mounted) return null;

  const normalizedSelected = (selectedCityName || '').trim().toLowerCase();

  return createPortal(
    <div
      className={styles.backdrop}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="city-modal-title"
    >
      <div
        className={styles.modalCard}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Botón Cerrar (X) */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 sm:top-6 sm:right-6 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center transition-colors cursor-pointer focus:outline-none z-10"
          aria-label="Cerrar modal"
        >
          <span className="material-symbols-outlined text-[18px]">close</span>
        </button>

        {/* Encabezado: Icono Badge y Título */}
        <div className="flex items-center gap-3 pr-10">
          <div className={styles.iconBadge}>
            <span className="material-symbols-outlined text-[22px] sm:text-[24px]">location_on</span>
          </div>
          <div>
            <h2
              id="city-modal-title"
              className="text-lg sm:text-2xl font-bold text-gray-900 leading-tight"
            >
              Selecciona Tu ciudad o municipio
            </h2>
            {(stateName || countryName) && (
              <p className="text-xs text-gray-500 mt-0.5">
                Mostrando ciudades de{' '}
                <span className="font-semibold text-purple-700">
                  {stateName ? `${stateName}, ` : ''}{countryName}
                </span>
              </p>
            )}
          </div>
        </div>

        {/* Barra de Búsqueda con Iconos Blindados contra Colapso */}
        <div className={styles.searchContainer}>
          <div className={styles.searchIconWrapper}>
            <span className="material-symbols-outlined text-[20px] pointer-events-none select-none">
              search
            </span>
          </div>
          <input
            ref={searchInputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar ciudad o municipio..."
            className={styles.searchInput}
          />
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                searchInputRef.current?.focus();
              }}
              className={styles.clearSearchBtn}
              aria-label="Borrar búsqueda"
              title="Borrar búsqueda"
            >
              <svg
                className="w-3.5 h-3.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2.5"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Grilla de Ciudades en 2 Columnas con Scroll Personalizado (#c084fc / #f3e8ff) */}
        <div
          data-scrollable="true"
          className={`w-full flex-1 ${styles.cityListScroll}`}
        >
          {!stateName ? (
            <div className="py-10 text-center text-gray-500">
              <span className="material-symbols-outlined text-4xl text-gray-300 mb-2">
                pin_drop
              </span>
              <p className="text-sm font-semibold text-gray-800">
                Primero selecciona tu estado o departamento
              </p>
              <p className="text-xs text-gray-400 mt-1 max-w-xs mx-auto">
                Para mostrarte las ciudades correspondientes, necesitamos saber en qué departamento o provincia te encuentras.
              </p>
              {onOpenStateModal && (
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    onOpenStateModal();
                  }}
                  className="mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-semibold shadow-xs cursor-pointer transition-colors"
                >
                  Seleccionar estado o departamento
                </button>
              )}
            </div>
          ) : isLoading ? (
            <div className="py-12 flex flex-col items-center justify-center text-gray-400 gap-2">
              <span className="material-symbols-outlined animate-spin text-3xl text-purple-600">
                progress_activity
              </span>
              <p className="text-sm font-medium">Cargando ciudades y municipios...</p>
            </div>
          ) : filteredCities.length > 0 ? (
            <div className={styles.cityGrid}>
              {filteredCities.map((ct) => {
                const cityName = ct.label || ct.value;
                const isSelected = normalizedSelected === cityName.toLowerCase();

                return (
                  <button
                    key={ct.value}
                    type="button"
                    onClick={() => {
                      onSelect(cityName);
                      onClose();
                    }}
                    className={`${styles.cityButton} ${
                      isSelected ? styles.cityButtonSelected : ''
                    }`}
                  >
                    <span className="truncate pr-2">{cityName}</span>
                    {isSelected && (
                      <span className="material-symbols-outlined text-[18px] text-[#9333ea] font-bold flex-shrink-0">
                        check
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ) : cities.length === 0 ? (
            <div className="py-10 text-center text-gray-500">
              <span className="material-symbols-outlined text-4xl text-gray-300 mb-2">
                location_city
              </span>
              <p className="text-sm font-semibold text-gray-800">
                No encontramos ciudades predefinidas para {stateName}
              </p>
              <p className="text-xs text-gray-400 mt-1 max-w-xs mx-auto">
                Puedes escribir el nombre de tu ciudad o municipio en la barra de búsqueda superior y seleccionarlo.
              </p>
              {search.trim() && (
                <button
                  type="button"
                  onClick={() => {
                    onSelect(search.trim());
                    onClose();
                  }}
                  className="mt-4 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-semibold shadow-xs cursor-pointer transition-colors"
                >
                  Usar &quot;{search.trim()}&quot; como mi ciudad
                </button>
              )}
            </div>
          ) : (
            <div className="py-12 text-center text-gray-500">
              <span className="material-symbols-outlined text-4xl text-gray-300 mb-2">
                search_off
              </span>
              <p className="text-sm font-medium">
                No encontramos ninguna ciudad o municipio que coincida con &quot;{search}&quot;
              </p>
              {search.trim() && (
                <button
                  type="button"
                  onClick={() => {
                    onSelect(search.trim());
                    onClose();
                  }}
                  className="mt-3 inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 rounded-xl text-xs font-semibold cursor-pointer transition-colors"
                >
                  <span className="material-symbols-outlined text-sm">add</span>
                  <span>Usar &quot;{search.trim()}&quot;</span>
                </button>
              )}
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="text-xs font-semibold text-[#c25ee8] hover:underline cursor-pointer"
                >
                  Ver todas las ciudades
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
